<template>
  <modal :open="internalOpen" :full-width="true" :full-height="true">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('pages.images.verifications.verificationModal.title') }}</h1>
      </modal-header>
    </template>

    <div class="pt-8 h-full">
      <div class="flex flex-col justify-center w-full h-full">
        <div v-if="loading" class="flex justify-center">
          <Spinner />
        </div>
        <div v-else class="grow">
          <div class="pb-3">
            {{
              $t('pages.images.verifications.verificationModal.count', [
                currentIndex + 1,
                totalNumberOfVerifications,
              ], totalNumberOfVerifications)
            }}
          </div>
          <Verification-comp v-model:verification="verifications[currentIndex]" v-model:loading-verified="loadingVerified" @verified="next()" />
        </div>
        <div v-if="!loading && totalNumberOfVerifications > 1" class="flex justify-between w-full">
          <custom-button
            variant="text"
            class="mt-15"
            :disabled="loadingVerified"
            @click="previous()"
          >
            {{ `< ${$t('back')}` }}
          </custom-button>
          <custom-button
            variant="text"
            class="mt-15"
            :disabled="loadingVerified"
            @click="next()"
          >
            {{ `${$t('next')} >` }}
          </custom-button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, ref, unref, watch,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Modal from '@utils/Modal/Modal.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { type ProviderParams, useProvider } from '@utils/table/hooks/provider';
import Spinner from '@utils/Spinner.vue';
import PhotoRating from '@pages/images/photos/PhotoRating.vue';
import VerificationComp from '@pages/images/verifications/Verification.vue';
import type { HydraCollection, Verification } from '@/entities/Entities';

interface Props {
  open: boolean;
  providerParams: ProviderParams;
  openOnVerificationId?: string;
}

const loadingVerified = ref(false);

const emit = defineEmits(['update:open', 'close']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const currentIndex = ref(0);

const providerParams = computed(() => ({
  ...props.providerParams,
  filter: {
    ...props.providerParams.filter,
    pagination: false,
  },
}));

const verifications = ref<Verification[]>([]);
const totalNumberOfVerifications = computed(() => verifications.value.length);

const { data, loading, refetch } = useProvider<HydraCollection<Verification>>('/verifications', providerParams, {
  disabled: true,
  immediate: false,
});

const loadData = async () => {
  loading.value = true;
  await refetch();
  loading.value = false;
  verifications.value = unref(data)?.['hydra:member'] ?? [];
  if (props.openOnVerificationId) {
    currentIndex.value = Math.max(verifications.value.findIndex((verification) => verification.id === props.openOnVerificationId), 0);
  } else {
    currentIndex.value = 0;
  }
};

watch(internalOpen, (newValue, oldValue) => {
  if (newValue && !oldValue) {
    loadData();
  }
});

function closeModal() {
  internalOpen.value = false;
  verifications.value = [];
  emit('close');
}

function previous() {
  if (currentIndex.value > 0) {
    currentIndex.value--;
  } else {
    currentIndex.value = totalNumberOfVerifications.value - 1;
  }
}

function next() {
  if (currentIndex.value < totalNumberOfVerifications.value - 1) {
    currentIndex.value++;
  } else {
    currentIndex.value = 0;
  }
}
</script>

<style lang="scss" scoped></style>

